import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import OfflineSyncBadge from "@/components/OfflineSyncBadge";
import Layout from "@/components/Layout";
import ClientLayout from "@/components/ClientLayout";
import { useIsMobile } from "@/lib/useIsMobile";
import { LayoutDashboard, Ship, FileText } from "lucide-react";
import AcceptInvite from "@/pages/AcceptInvite";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import Quotes from "@/pages/Quotes";
import QuoteEditor from "@/pages/QuoteEditor";
import QuotePrint from "@/pages/QuotePrint";
import PublicProposal from "@/pages/PublicProposal";
import Clients from "@/pages/Clients";
import Vessels from "@/pages/Vessels";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import VesselCompare from "@/pages/VesselCompare";
import VesselsAudit from "@/pages/VesselsAudit";
import VesselFlyer from "@/pages/VesselFlyer";
import VesselDetail from "@/pages/VesselDetail";
import Products from "@/pages/Products";
import Tenders from "@/pages/Tenders";
import Staff from "@/pages/Staff";
import Settings from "@/pages/Settings";
import PreviewAsClientBanner from "@/components/PreviewAsClientBanner";

// Nav bundle staff see when the app is rendered in the mobile shell. Kept
// intentionally short — Vessels + Quotes only, view-only, mirrors the
// product decision that field staff can't edit on their phones.
const STAFF_MOBILE_NAV = [
  { to: "/vessels", label: "Vessels", icon: Ship, testid: "staff-nav-vessels" },
  { to: "/quotes", label: "Quotes", icon: FileText, testid: "staff-nav-quotes" },
];

// Picks the right shell at runtime: ClientLayout (no sidebar) for clients,
// regular Layout (sidebar) for admin/staff. This way /vessels can be a
// single route while still rendering the right chrome per role.
// Staff on mobile see a view-only client-style shell with Vessels + Quotes.
function RoleLayout() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  if (user?.role === "client") return <ClientLayout />;
  if (user?.role === "staff" && isMobile) {
    return (
      <>
        <OfflineSyncBadge />
        <ClientLayout navItems={STAFF_MOBILE_NAV} brandLabel="Staff · View only" />
      </>
    );
  }
  return (
    <>
      <OfflineSyncBadge />
      <Layout />
    </>
  );
}

// Tiny redirect for the root path: clients → client dashboard, staff on
// mobile → vessels (their landing tab), everyone else → admin Dashboard.
function Home() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  if (user?.role === "client") return <ClientDashboard />;
  if (user?.role === "staff" && isMobile) return <Navigate to="/vessels" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
        <PreviewAsClientBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/proposal/:token" element={<PublicProposal />} />
          <Route
            path="/quotes/:id/print"
            element={
              <ProtectedRoute requiredPermission="quotes">
                <QuotePrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vessels/:id/flyer"
            element={
              <ProtectedRoute requiredPermission="vessels" clientAllowed>
                <VesselFlyer />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute clientAllowed>
                <RoleLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Home />} />
            <Route
              path="/quotes"
              element={
                <ProtectedRoute requiredPermission="quotes">
                  <Quotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/new"
              element={
                <ProtectedRoute requiredPermission="quotes">
                  <QuoteEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/:id"
              element={
                <ProtectedRoute requiredPermission="quotes">
                  <QuoteEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute requiredPermission="clients">
                  <Clients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vessels"
              element={
                <ProtectedRoute requiredPermission="vessels" clientAllowed>
                  <Vessels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vessels/compare"
              element={
                <ProtectedRoute requiredPermission="vessels" clientAllowed>
                  <VesselCompare />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vessels/audit"
              element={
                <ProtectedRoute adminOnly>
                  <VesselsAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vessels/:id"
              element={
                <ProtectedRoute requiredPermission="vessels" clientAllowed>
                  <VesselDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute requiredPermission="products">
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenders"
              element={
                <ProtectedRoute requiredPermission="tenders">
                  <Tenders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute requiredPermission="projects">
                  <Projects />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute requiredPermission="projects">
                  <ProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute adminOnly>
                  <Staff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute adminOnly>
                  <Settings />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </ConfirmProvider>
      <Toaster position="top-right" theme="dark" />
    </AuthProvider>
  );
}
